import React, { useState, useEffect } from 'react';
import { Modal, Button, Table } from 'react-bootstrap';
import { CarrierServiceItem, CarrierStore } from '../../services/admin.carrier.service';
import styles from '../../styles/components/CarrierStoresModal.module.css';
import modalStyles from '../../styles/common/Modal.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { countryService } from '../../services/country.service';

interface CarrierStoresModalProps {
    show: boolean;
    onHide: () => void;
    service: CarrierServiceItem | null;
}

const CarrierStoresModal: React.FC<CarrierStoresModalProps> = ({ show, onHide, service }) => {
    const [activeTab, setActiveTab] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [countryNamesMap, setCountryNamesMap] = useState<{ [key: string]: string }>({});

    const storesGrouped = service?.stores || {};
    const countries = Object.keys(storesGrouped);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const data = await countryService.getAllCountries();
                const map: { [key: string]: string } = {};
                data.forEach((c) => {
                    if (c.iso2Country) {
                        map[c.iso2Country.toUpperCase()] = c.name;
                    }
                });
                setCountryNamesMap(map);
            } catch (error) {
                console.error("Error fetching countries in CarrierStoresModal:", error);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        if (countries.length > 0) {
            setActiveTab(countries[0]);
        } else {
            setActiveTab('');
        }
        setSearchTerm('');
    }, [service]);

    const activeStores: CarrierStore[] = activeTab ? storesGrouped[activeTab] || [] : [];

    // Filter stores based on search term
    const filteredStores = activeStores.filter(store => {
        const term = searchTerm.toLowerCase().trim();
        if (!term) return true;
        
        const nombre = store.nombre ? store.nombre.toLowerCase() : '';
        const zipcode = store.zipcode ? store.zipcode.toLowerCase() : '';
        
        return nombre.includes(term) || zipcode.includes(term);
    });

    const getCountryName = (code: string) => {
        const uppercaseCode = code.toUpperCase();
        return countryNamesMap[uppercaseCode] || uppercaseCode;
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            className={styles.editServiceModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    Tiendas Disponibles - {service?.carrier}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className={styles.filterContainer}>
                    {countries.length > 1 && (
                        <div className={styles.selectWrapper}>
                            <label className={styles.fieldLabel}>
                                Filtrar por País:
                            </label>
                            <div className={styles.selectFlexContainer}>
                                {activeTab && (
                                    <img
                                        src={`https://flagcdn.com/w40/${activeTab.toLowerCase()}.png`}
                                        width="24"
                                        height="16"
                                        alt={activeTab}
                                        className={styles.flagImage}
                                    />
                                )}
                                <select
                                    value={activeTab}
                                    onChange={(e) => {
                                        setActiveTab(e.target.value);
                                        setSearchTerm('');
                                    }}
                                    className={styles.selectDropdown}
                                >
                                    {countries.map((country) => (
                                        <option key={country} value={country}>
                                            {getCountryName(country)} ({storesGrouped[country].length} tiendas)
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {countries.length === 1 && (
                        <div className={styles.singleCountryHeader}>
                            <img
                                src={`https://flagcdn.com/w40/${countries[0].toLowerCase()}.png`}
                                width="20"
                                height="14"
                                alt={countries[0]}
                                className={styles.flagImage}
                            />
                            <span className={styles.singleCountryText}>
                                Tiendas en {getCountryName(countries[0])} ({storesGrouped[countries[0]].length})
                            </span>
                        </div>
                    )}

                    {/* Search Input Box */}
                    <div className={styles.searchContainer}>
                        <label className={styles.fieldLabel}>
                            Buscar Tienda:
                        </label>
                        <div className={styles.searchBarWrapper}>
                            <span className={styles.searchIcon}>
                                <FontAwesomeIcon icon={faSearch} />
                            </span>
                            <input
                                type="text"
                                placeholder="Buscar por nombre o código postal..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.searchInput}
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className={styles.clearButton}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {filteredStores.length > 0 ? (
                    <div className={styles.tableContainer}>
                        <Table hover responsive className={styles.table}>
                            <thead className={styles.tableHeader}>
                                <tr>
                                    <th>Nombre de Tienda</th>
                                    <th style={{ width: '30%', textAlign: 'center' }}>Código Postal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStores.map((store, index) => (
                                    <tr key={index}>
                                        <td className={styles.storeNameCell}>
                                            {store.nombre}
                                        </td>
                                        <td className={styles.zipCodeCell}>
                                            {store.zipcode || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        {searchTerm ? 'No se encontraron tiendas que coincidan con la búsqueda.' : 'No hay tiendas registradas para este transportista.'}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                <Button 
                    variant="secondary" 
                    onClick={onHide} 
                    className={styles.secondaryBtn}
                >
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CarrierStoresModal;
